import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { SKIP_AUTH_REFRESH } from 'app/core/interceptors/http-context-tokens';
import { AuthService } from 'app/modules/auth/services/auth.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { redact } from 'app/shared/utils/sensitive-keys';
import { DataStorageService } from 'app/shared/utils/data-storage-service';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private router: Router,
    private dataStorageService: DataStorageService,
    private injector: Injector,
    private logger: LoggerService
  ) {}

  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const token = this.dataStorageService.get('token');

    if (token) {
      req = req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
    }

    const startedAt = Date.now();
    this.logger.debug('API', `-> ${req.method} ${req.url}`, redact(req.body));

    return next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.logger.debug(
            'API',
            `<- ${event.status} ${req.method} ${req.url} (${Date.now() - startedAt}ms)`,
            redact(event.body)
          );
        }
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.warn(
          'API',
          `<- ${error.status} ${req.method} ${req.url} (${Date.now() - startedAt}ms)`,
          redact(error.error)
        );

        if (error.status >= 500) {
          Sentry.captureMessage(`API server error: ${req.method} ${req.url}`, {
            level: 'error',
            extra: { status: error.status, response: redact(error.error) },
          });
        }

        if (error.status === 401 && !req.context.get(SKIP_AUTH_REFRESH)) {
          return this.handle401(req, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((response) => {
          this.isRefreshing = false;
          const newToken = response.data.token;
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addToken(req, newToken));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.dataStorageService.remove('token');
          this.router.navigate(['/auth/login']);
          return throwError(() => error);
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addToken(req, token!)))
    );
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }
}
